import type { EntityData } from '@adornis/baseql/entities/types.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import { School } from '@chancenwerk/master-data-management/partner/entities/School.js';
import {
  PartnerActiveState,
  PartnerFilter,
} from '../../master-data-management/partner/api/subscribePartnerByFilter.js';
import { PartnerActiveAspect } from '../../master-data-management/partner/entities/aspects/PartnerActiveAspect.js';
import { Foundation } from '../../master-data-management/partner/entities/Foundation.js';

const resolveGetPublicPartnerInformationByID = <T = EntityData<Partner>>(
  ids?: string[],
  onlyActive?: boolean,
  classes?: string[],
) => {
  return async (gqlFields: BaseQLSelectionSet<Partner>): Promise<T[]> => {
    const partnerCollection = await getRawCollection<EntityData<Partner>>(Partner._collectionName);
    const selector = {
      ...(ids ? { _id: { $in: ids } } : {}),
      ...(classes ? { _class: { $in: classes } } : {}),
    };
    return (await partnerCollection
      .find(selector, {
        projection: {
          _id: 1,
          _class: 1,
          name: 1,
          address: {
            _class: 1,
            strasse: 1,
            country: 1,
            zipCity: {
              _class: 1,
              zip: 1,
              city: 1,
            },
          },
          mediaID: 1,
          bundesland: 1,
          media: AdornisFile.allFields,
          url: 1,
          hideOnWebsite: 1,
        },
      })
      .toArray()) as T[];
  };
};

const resolveGetPublicPartnerActive = <T = EntityData<Partner>>() => {
  return async (gqlFields: BaseQLSelectionSet<Partner>): Promise<T[]> => {
    const partnerCollection = await getRawCollection<EntityData<Partner>>(Partner._collectionName);
    const filter = new PartnerFilter({});
    filter.setFilterAspect(new PartnerActiveAspect({ value: PartnerActiveState.YES }));
    // const result = (await partnerCollection.aggregate([...searchPartnerAggregation(filter)]).toArray()) as T[];
    const result = (await partnerCollection.find(filter.getMongoFilter()).toArray()) as T[];
    return result;
  };
};

const getPublicPartnerInformationByID = registerQuery({
  type: () => [Partner],
  operationName: 'getPublicPartnerInformationByID',
  params: [{ type: () => [String], name: 'schoolIDs' }],
  resolve: resolveGetPublicPartnerInformationByID<Partner>,
});

const getAllPublicPartnerInformation = registerQuery({
  type: () => [Partner],
  operationName: 'getAllPublicPartnerInformation',
  resolve: resolveGetPublicPartnerInformationByID<Partner>,
});

const getAllPublicSchoolInformation = registerQuery({
  type: () => [School],
  operationName: 'getAllPublicSchoolInformation',
  resolve: () => async (gqlFields: BaseQLSelectionSet<School>) => {
    return resolveGetPublicPartnerInformationByID<Partner>(undefined, undefined, [School._class])(gqlFields);
  },
});

const getAllPublicParternActive = registerQuery({
  type: () => [Partner],
  operationName: 'getAllPublicPartnerActive',
  resolve: resolveGetPublicPartnerActive<Partner>,
});

const getAllFoundationInformation = registerQuery({
  type: () => [Foundation],
  operationName: 'getAllFoundationInformation',
  resolve: () => (gqlFields: BaseQLSelectionSet<Foundation>) =>
    resolveGetPublicPartnerInformationByID<Foundation>(undefined, undefined, [Foundation._class])(gqlFields),
});

export namespace PartnerPublicInformationAPI {
  export const getAll = getAllPublicPartnerInformation;
  export const getAllSchool = getAllPublicSchoolInformation;
  export const getByID = getPublicPartnerInformationByID;
  export const getAllActive = getAllPublicParternActive;
  export const getAllFoundation = getAllFoundationInformation;
}
